import type { Transaction } from "@money/shared";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatCents } from "../lib/format";

interface Point {
  ts: number;
  label: string;
  balance: number;
}

export function BalanceChart({
  transactions,
  startingBalanceCents,
}: {
  transactions: Transaction[];
  startingBalanceCents: number;
}) {
  if (transactions.length === 0) {
    return (
      <div className="h-48 flex items-center justify-center text-slate-500 text-sm">
        No transactions yet — add one in Settings.
      </div>
    );
  }

  // Build a cumulative-balance series oldest → newest.
  const sorted = [...transactions].sort((a, b) => a.posted_at - b.posted_at);
  let running = startingBalanceCents - sorted.reduce((s, t) => s + t.amount_cents, 0);
  const points: Point[] = sorted.map((tx) => {
    running += tx.amount_cents;
    return {
      ts: tx.posted_at,
      label: new Date(tx.posted_at).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
      balance: running / 100,
    };
  });

  return (
    <div className="h-48">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={points} margin={{ left: 8, right: 8, top: 8, bottom: 0 }}>
          <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
          <XAxis dataKey="label" stroke="#64748b" tick={{ fontSize: 11 }} />
          <YAxis
            stroke="#64748b"
            tick={{ fontSize: 11 }}
            tickFormatter={(v: number) => formatCents(v * 100)}
            width={70}
          />
          <Tooltip
            contentStyle={{
              background: "#0f172a",
              border: "1px solid #1e293b",
              borderRadius: 6,
              fontSize: 12,
            }}
            formatter={(v: number) => formatCents(v * 100)}
          />
          <Line
            type="monotone"
            dataKey="balance"
            stroke="#60a5fa"
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
